// File: frontend/src/pages/KnowledgeGraph/About.tsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import HubIcon from '@mui/icons-material/Hub';
import TimelineIcon from '@mui/icons-material/Timeline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RouteIcon from '@mui/icons-material/Route';
import StatsBar from './components/StatsBar';
import { useConstellation } from './hooks/useConstellation';
import { cyber } from '../../theme';

const KnowledgeGraphAbout: React.FC = () => {
  const { stats, statsLoading } = useConstellation();

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
      <Paper sx={{ p: 4, mb: 3, border: `1px solid ${cyber.border}`, boxShadow: 'none' }}>
        {/* Back link */}
        <Link
          component={RouterLink}
          to="/knowledge-graph"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 3, fontWeight: 500 }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to STIX Constellation
        </Link>

        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          What is STIX Constellation?
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Section 1: The Problem */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          The Problem
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          Each threat intelligence report is an island. When one report discusses "Fancy Bear" and
          another discusses "APT28", they appear unrelated — even though they describe the same
          Russian threat actor. TI Mindmap HUB processes each report independently, producing rich
          structured data for each, but with no connections between them.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Section 2: The Solution */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          The Solution — A Graph of Graphs
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          STIX Constellation is a Knowledge Graph that connects all reports into a single, unified
          intelligence picture. It works in two layers:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <SearchIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Evidence Layer"
              secondary="Preserves what each report says, exactly as extracted. Every claim is tied to its source."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <HubIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Knowledge Layer"
              secondary={`Unifies entities across reports. "Fancy Bear", "APT28", "Forest Blizzard", and "STRONTIUM" all point to the same canonical entity.`}
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Section 3: How It Works */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          How It Works
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          Three-level entity resolution:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="1. Exact matching"
              secondary="Same name, same STIX ID → same entity (99.4% of cases)"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2. Alias lookup"
              secondary="MITRE ATT&CK authoritative aliases"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3. AI-powered similarity"
              secondary="LLM evaluates ambiguous cases"
            />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Plus: <strong>IOC Protection</strong> (indicators never merge by similarity) and{' '}
          <strong>Entity Blacklist</strong>.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Section 4: What You Can Do */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          What You Can Do
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><SearchIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Search for any threat actor, malware, tool, or technique" />
          </ListItem>
          <ListItem>
            <ListItemIcon><HubIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Explore connections visually" />
          </ListItem>
          <ListItem>
            <ListItemIcon><TimelineIcon color="primary" /></ListItemIcon>
            <ListItemText primary="See how an entity evolved across reports over time" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CompareArrowsIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Discover which reports are connected through shared entities" />
          </ListItem>
          <ListItem>
            <ListItemIcon><RouteIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Find attack paths from ATT&CK techniques to actors and tools" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Section 5: Current Status */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Current Status
        </Typography>
        <StatsBar stats={stats} loading={statsLoading} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The graph updates automatically every 15 minutes as new reports are processed.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Section 6: Beta & Feedback */}
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Beta & Feedback
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          This feature is in active development. We are working on: enhanced visualization, Weekly
          Threat Briefing integration, advanced analysis tools. Your feedback helps us prioritize.
        </Typography>
        <Link component={RouterLink} to="/feedback" sx={{ fontWeight: 500 }}>
          Share your feedback →
        </Link>
      </Paper>
    </Box>
  );
};

export default KnowledgeGraphAbout;
